/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2026-04-30
 * Description: Post-verification landing. The user already has a valid docs
 *              cookie at this point — they can immediately go to the API
 *              docs. We optionally offer authenticator-app enrollment, or
 *              show "Auth App is active" if already enrolled.
 */

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@services/api'

const DOCS_URL = '/api/docs/external/'

const ApiAccessWelcome: React.FC = () => {
  const [enrolled, setEnrolled] = useState<boolean | null>(null)

  useEffect(() => {
    api
      .accessRequestMe()
      .then((me) => setEnrolled(me.totp_enrolled))
      .catch(() => setEnrolled(false))
  }, [])

  return (
    <div className="mx-auto max-w-xl py-16">
      <div className="rounded-lg border border-green-200 bg-green-50 p-6 dark:border-green-800/50 dark:bg-green-900/30">
        <h1 className="text-xl font-semibold text-green-900 dark:text-green-300">You&apos;re in</h1>
        <p className="mt-2 text-sm text-green-800 dark:text-green-300">
          Your email is verified and you have access to the Comcast Business DAL-ify API documentation.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <a
          href={DOCS_URL}
          className="flex flex-col rounded-lg border border-indigo-300 bg-white p-5 shadow-sm transition hover:border-indigo-500 hover:shadow dark:bg-gray-800 dark:shadow-none"
        >
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Continue to API docs</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Open the Sonata + TMF Swagger UI now.</p>
          <span className="mt-3 inline-flex items-center text-sm font-medium text-indigo-600">Open docs →</span>
        </a>

        {enrolled ? (
          <Link
            to="/api-access/auth-setup"
            className="flex flex-col rounded-lg border border-green-300 bg-green-50 p-5 shadow-sm transition hover:border-green-500 hover:shadow dark:bg-green-900/30 dark:shadow-none"
          >
            <h2 className="text-base font-semibold text-green-900 dark:text-green-300">
              Auth App is active on this device <span className="ml-1">✓</span>
            </h2>
            <p className="mt-1 text-sm text-green-800 dark:text-green-300">
              Sign in with the 6-digit code from your authenticator. Email codes still work too.
            </p>
            <span className="mt-3 inline-flex items-center text-xs font-medium text-green-700 dark:text-green-300">
              Manage / re-enroll →
            </span>
          </Link>
        ) : (
          <Link
            to="/api-access/auth-setup"
            className="flex flex-col rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition hover:border-gray-400 hover:shadow dark:border-gray-700 dark:bg-gray-800 dark:shadow-none"
          >
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Set up authenticator app{' '}
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(optional)</span>
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Skip email codes on future logins. Scan a QR with Google Authenticator, Microsoft Authenticator,
              1Password, etc.
            </p>
            <span className="mt-3 inline-flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              Set up →
            </span>
          </Link>
        )}
      </div>

      <p className="mt-8 text-xs text-gray-500 dark:text-gray-400">
        Only one authenticator device is supported. Re-enrolling will invalidate the previous device immediately.
      </p>
    </div>
  )
}

export default ApiAccessWelcome
